import React, { useState } from 'react';
import { Bike, Store, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RiderForm = ({ onSubmit }) => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', city: '', vehicle: '', note: '' });
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.city || !form.vehicle) return;
    setLoading(true);
    await onSubmit(form);
    setLoading(false);
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="r-name">Nome e cognome *</Label>
          <Input id="r-name" name="name" value={form.name} onChange={handleChange} placeholder="Mario Rossi" required />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="r-phone">Telefono *</Label>
          <Input id="r-phone" name="phone" value={form.phone} onChange={handleChange} placeholder="+39 333 1234567" required />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="r-email">Email *</Label>
          <Input id="r-email" type="email" name="email" value={form.email} onChange={handleChange} placeholder="mario@email.it" required />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="r-city">Città *</Label>
          <Input id="r-city" name="city" value={form.city} onChange={handleChange} placeholder="Cosenza" required />
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label>Mezzo di trasporto *</Label>
          <Select value={form.vehicle} onValueChange={(v) => setForm({ ...form, vehicle: v })}>
            <SelectTrigger>
              <SelectValue placeholder="Seleziona il tuo mezzo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Bicicletta">Bicicletta</SelectItem>
              <SelectItem value="E-bike">E-bike</SelectItem>
              <SelectItem value="Scooter / Moto">Scooter / Moto</SelectItem>
              <SelectItem value="Auto">Auto</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="r-note">Messaggio (opzionale)</Label>
          <Textarea id="r-note" name="note" value={form.note} onChange={handleChange} placeholder="Disponibilità, esperienza..." rows={3} />
        </div>
      </div>
      <DialogFooter>
        <Button type="submit" disabled={loading} className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-primary-foreground font-semibold">
          {loading ? 'Invio...' : 'Invia candidatura'}
        </Button>
      </DialogFooter>
    </form>
  );
};

const PartnerForm = ({ onSubmit }) => {
  const [form, setForm] = useState({ restaurant: '', contact: '', email: '', phone: '', city: '', cuisine: '', note: '' });
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.restaurant || !form.contact || !form.email || !form.phone || !form.city) return;
    setLoading(true);
    await onSubmit(form);
    setLoading(false);
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="p-restaurant">Nome del locale *</Label>
          <Input id="p-restaurant" name="restaurant" value={form.restaurant} onChange={handleChange} placeholder="Pizzeria Da Mario" required />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="p-contact">Referente *</Label>
          <Input id="p-contact" name="contact" value={form.contact} onChange={handleChange} placeholder="Mario Rossi" required />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="p-email">Email *</Label>
          <Input id="p-email" type="email" name="email" value={form.email} onChange={handleChange} placeholder="info@locale.it" required />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="p-phone">Telefono *</Label>
          <Input id="p-phone" name="phone" value={form.phone} onChange={handleChange} placeholder="+39 0984 123456" required />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="p-city">Città *</Label>
          <Input id="p-city" name="city" value={form.city} onChange={handleChange} placeholder="Cosenza" required />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="p-cuisine">Tipo di cucina</Label>
          <Input id="p-cuisine" name="cuisine" value={form.cuisine} onChange={handleChange} placeholder="Pizza, Pasta..." />
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="p-note">Messaggio (opzionale)</Label>
          <Textarea id="p-note" name="note" value={form.note} onChange={handleChange} placeholder="Raccontaci del tuo locale..." rows={3} />
        </div>
      </div>
      <DialogFooter>
        <Button type="submit" disabled={loading} className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-primary-foreground font-semibold">
          {loading ? 'Invio...' : 'Invia richiesta'}
        </Button>
      </DialogFooter>
    </form>
  );
};

const PromoBanners = () => {
  const [riderOpen, setRiderOpen] = useState(false);
  const [partnerOpen, setPartnerOpen] = useState(false);
  const { toast } = useToast();

  const handleRider = async (form) => {
    try {
      await axios.post(`${API}/applications/rider`, form);
      setRiderOpen(false);
      toast({ title: 'Candidatura inviata!', description: `Grazie ${form.name.split(' ')[0]}, ti contatteremo presto via email.` });
    } catch (e) {
      toast({ title: 'Errore', description: "Invio non riuscito. Riprova tra poco.", variant: 'destructive' });
    }
  };
  const handlePartner = async (form) => {
    try {
      await axios.post(`${API}/applications/partner`, form);
      setPartnerOpen(false);
      toast({ title: 'Richiesta inviata!', description: `Grazie! Ti contatteremo per attivare ${form.restaurant} su Italian Fast Delivery.` });
    } catch (e) {
      toast({ title: 'Errore', description: "Invio non riuscito. Riprova tra poco.", variant: 'destructive' });
    }
  };

  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="relative overflow-hidden rounded-3xl bg-foreground text-white p-8 sm:p-10 min-h-[260px] flex flex-col justify-between">
          <div className="relative z-10 max-w-sm">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary mb-4">
              <Bike className="h-6 w-6" />
            </div>
            <h3 className="font-display text-2xl font-bold">Diventa un rider</h3>
            <p className="mt-2 text-white/70">Guadagna con orari flessibili consegnando nella tua città. Iscriviti oggi stesso.</p>
          </div>
          <Button onClick={() => setRiderOpen(true)} className="relative z-10 mt-6 w-fit gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold">
            Candidati ora <ArrowRight className="h-4 w-4" />
          </Button>
          <img src="https://images.pexels.com/photos/11206901/pexels-photo-11206901.jpeg?auto=compress&cs=tinysrgb&w=600" alt="" className="absolute right-0 bottom-0 h-full w-1/2 object-cover opacity-30" />
        </div>

        <div className="relative overflow-hidden rounded-3xl bg-accent p-8 sm:p-10 min-h-[260px] flex flex-col justify-between">
          <div className="relative z-10 max-w-sm">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary mb-4">
              <Store className="h-6 w-6 text-white" />
            </div>
            <h3 className="font-display text-2xl font-bold text-foreground">Hai un ristorante?</h3>
            <p className="mt-2 text-accent-foreground/80">Aumenta le vendite raggiungendo migliaia di clienti affamati. Unisciti a Italian Fast Delivery.</p>
          </div>
          <Button onClick={() => setPartnerOpen(true)} className="relative z-10 mt-6 w-fit gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold">
            Diventa partner <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <Dialog open={riderOpen} onOpenChange={setRiderOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Bike className="h-6 w-6 text-primary" /> Candidati come rider
            </DialogTitle>
            <DialogDescription>
              Compila il modulo: ti ricontatteremo per i prossimi passi.
            </DialogDescription>
          </DialogHeader>
          <RiderForm onSubmit={handleRider} />
        </DialogContent>
      </Dialog>

      <Dialog open={partnerOpen} onOpenChange={setPartnerOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Store className="h-6 w-6 text-primary" /> Diventa partner
            </DialogTitle>
            <DialogDescription>
              Inserisci i dati del tuo locale: ti aiuteremo ad attivarlo sulla piattaforma.
            </DialogDescription>
          </DialogHeader>
          <PartnerForm onSubmit={handlePartner} />
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default PromoBanners;
